import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useNavigate, useLocation, matchPath } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiClient } from "@/services/api";
import { isTokenExpired, shouldEndSessionOnError } from "@/utils/auth";
import { useToast } from "@/hooks/useToast";
import { analytics } from "@/utils/analytics";
import { appRoutes } from "@/routesConfig";
import type { User, LoginRequest, RegisterRequest } from "@/types";

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  register: (userData: RegisterRequest) => Promise<void>;
  logout: () => void;
  setSession: (user: User, token: string, refreshToken?: string) => void;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingAuthRedirect, setPendingAuthRedirect] = useState<string | null>(
    null,
  );
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation("auth");
  const toast = useToast();

  const applyUserLocale = (userData: User) => {
    if (userData.locale) {
      i18n.changeLanguage(userData.locale);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem("token");
      const storedUser = localStorage.getItem("user");

      if (token && storedUser) {
        try {
          // Check if token is expired
          if (isTokenExpired(token)) {
            // Token is expired, try to refresh
            const {
              user: refreshedUser,
              token: newToken,
              refreshToken: newRefreshToken,
            } = await apiClient.refreshToken();
            localStorage.setItem("token", newToken);
            if (newRefreshToken) {
              localStorage.setItem("refreshToken", newRefreshToken);
            }
            localStorage.setItem("user", JSON.stringify(refreshedUser));
            setUser(refreshedUser);
            applyUserLocale(refreshedUser);
          } else {
            // Token is valid, verify with server
            const currentUser = await apiClient.getCurrentUser();
            setUser(currentUser);
            applyUserLocale(currentUser);
          }
        } catch (error) {
          if (shouldEndSessionOnError(error)) {
            // The server rejected the credentials or the request timed out;
            // end the session.
            clearStoredAuth();
          } else {
            // Transient failure (offline reload, server outage): keep the
            // stored tokens and hydrate the user from the cached profile so
            // a network drop does not log the user out.
            hydrateUserFromCache(storedUser);
          }
        }
      }

      setIsLoading(false);
    };

    initAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user || !pendingAuthRedirect) {
      return;
    }

    navigate(pendingAuthRedirect, { replace: true });
    setPendingAuthRedirect(null);
  }, [navigate, pendingAuthRedirect, user]);

  const getSuccessfulAuthRedirect = () => {
    const savedLocation = (location.state as { from?: { pathname: string } })
      ?.from?.pathname;

    // Priority: 1) Saved location from RequireAuth (protected page), 2) Dashboard
    return savedLocation ?? "/dashboard";
  };

  const clearStoredAuth = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
  };

  const hydrateUserFromCache = (storedUser: string) => {
    try {
      const cachedUser = JSON.parse(storedUser) as User;
      setUser(cachedUser);
      applyUserLocale(cachedUser);
    } catch {
      // Unreadable cached profile: there is no usable session to keep.
      clearStoredAuth();
    }
  };

  const storeAuthData = (
    userData: User,
    token: string,
    refreshToken?: string
  ) => {
    localStorage.setItem("token", token);
    if (refreshToken) {
      localStorage.setItem("refreshToken", refreshToken);
    }
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
    applyUserLocale(userData);
  };

  const login = async (credentials: LoginRequest) => {
    try {
      const { user: userData, token, refreshToken } =
        await apiClient.login(credentials);

      storeAuthData(userData, token, refreshToken);
      setPendingAuthRedirect(getSuccessfulAuthRedirect());

      // Track successful login
      analytics.user.login();

    } catch (error: unknown) {
      const apiError = error as {
        response?: {
          status?: number;
          data?: {
            message?: string;
            error?: { code?: string; message?: string };
            data?: { changePasswordToken?: string };
          };
        };
      };

      // Handle PASSWORD_CHANGE_REQUIRED — redirect to change-password page
      if (
        apiError.response?.status === 403 &&
        apiError.response?.data?.error?.code === "PASSWORD_CHANGE_REQUIRED"
      ) {
        const changePasswordToken =
          apiError.response.data.data?.changePasswordToken;
        if (changePasswordToken) {
          navigate(`/change-password?token=${encodeURIComponent(changePasswordToken)}`, {
            replace: true,
          });
          return;
        }
      }

      const errorMessage =
        apiError.response?.data?.error?.message ||
        apiError.response?.data?.message ||
        t("auth:loginError");
      toast.error(errorMessage);
      throw error;
    }
  };

  const register = async (userData: RegisterRequest) => {
    try {
      const { user: newUser, token, refreshToken } =
        await apiClient.register(userData);

      storeAuthData(newUser, token, refreshToken);
      setPendingAuthRedirect(getSuccessfulAuthRedirect());

      // Track successful registration
      analytics.user.register();

    } catch (error: unknown) {
      const apiError = error as { response?: { data?: { message?: string } } };
      const errorMessage =
        apiError.response?.data?.message || t("auth:registerError");
      toast.error(errorMessage);
      throw error;
    }
  };

  const logout = () => {
    // Track logout before clearing data
    analytics.user.logout();

    // Revoke the refresh-token family server-side; best-effort only.
    const refreshToken = localStorage.getItem("refreshToken");
    if (refreshToken) {
      apiClient.logout(refreshToken).catch(() => {});
    }

    clearStoredAuth();
    setUser(null);

    // Check if current page is public
    const currentPath = location.pathname;
    const isPublicPage = appRoutes.some(
      (route) => route.public && matchPath(route.path, currentPath),
    );

    // Only redirect if on a protected page
    if (!isPublicPage) {
      navigate("/");
    }
  };

  const setSession = (
    sessionUser: User,
    token: string,
    refreshToken?: string
  ) => {
    storeAuthData(sessionUser, token, refreshToken);
  };

  const updateUser = (updatedUser: User) => {
    localStorage.setItem("user", JSON.stringify(updatedUser));
    setUser(updatedUser);
    applyUserLocale(updatedUser);
  };

  const value = {
    user,
    isLoading,
    login,
    register,
    logout,
    setSession,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
